/**
 * Session Configuration
 * 
 * Centralized configuration for session-related timeouts and settings.
 * 
 * Query Parameters:
 * - sessionTimeout=<milliseconds>: Override the default session timeout duration
 * - sessionExtensionTime=<milliseconds>: Override when the extension modal appears
 */

export const SESSION_CONFIG = {
  /**
   * Session timeout duration (in seconds)
   * How long the session is held before expiring
   */
  SESSION_TIMEOUT_SECONDS: 1200, // 20 minutes
  
  /**
   * Session extension time (in seconds)
   * When to show the extension modal (5 minutes before expiry)
   */
  SESSION_EXTENSION_TIME_SECONDS: 300, // 5 minutes
  
  /**
   * Debounce timeout for auto-saving session data (in milliseconds)
   * Prevents too many API calls when state changes rapidly
   */
  AUTO_SAVE_DEBOUNCE_MS: 1000,
  
  /**
   * Timeout for manual session saves (in milliseconds)
   * Used when explicitly saving session data
   */
  MANUAL_SAVE_TIMEOUT_MS: 2000,
  
  /**
   * Maximum retry attempts for failed session saves
   */
  MAX_RETRY_ATTEMPTS: 3,
  
  /**
   * Delay between retry attempts (in milliseconds)
   */
  RETRY_DELAY_MS: 500
} as const;

/**
 * Get the session timeout duration in seconds
 * Checks for sessionTimeout query parameter to override the default
 */
export function getSessionTimeoutSeconds(): number {
  // Check for sessionTimeout query parameter (in milliseconds)
  const urlParams = new URLSearchParams(window.location.search);
  const sessionTimeoutParam = urlParams.get('sessionTimeout');
  
  if (sessionTimeoutParam) {
    const timeoutMs = parseInt(sessionTimeoutParam, 10);
    if (!isNaN(timeoutMs) && timeoutMs > 0) {
      // Convert milliseconds to seconds
      return Math.floor(timeoutMs / 1000);
    }
  }
  
  return SESSION_CONFIG.SESSION_TIMEOUT_SECONDS;
}

/**
 * Get the session timeout duration in milliseconds
 * Checks for sessionTimeout query parameter to override the default
 */
export function getSessionTimeoutMs(): number {
  // Check for sessionTimeout query parameter (in milliseconds)
  const urlParams = new URLSearchParams(window.location.search);
  const sessionTimeoutParam = urlParams.get('sessionTimeout');
  
  if (sessionTimeoutParam) {
    const timeoutMs = parseInt(sessionTimeoutParam, 10);
    if (!isNaN(timeoutMs) && timeoutMs > 0) {
      return timeoutMs;
    }
  }
  
  return SESSION_CONFIG.SESSION_TIMEOUT_SECONDS * 1000;
}

/**
 * Get the session extension time in seconds
 * Checks for sessionExtensionTime query parameter to override the default
 */
export function getSessionExtensionTimeSeconds(): number {
  // Check for sessionExtensionTime query parameter (in milliseconds)
  const urlParams = new URLSearchParams(window.location.search);
  const sessionExtensionTimeParam = urlParams.get('sessionExtensionTime');
  
  if (sessionExtensionTimeParam) {
    const extensionTimeMs = parseInt(sessionExtensionTimeParam, 10);
    if (!isNaN(extensionTimeMs) && extensionTimeMs > 0) {
      // Convert milliseconds to seconds
      return Math.floor(extensionTimeMs / 1000);
    }
  }
  
  return SESSION_CONFIG.SESSION_EXTENSION_TIME_SECONDS;
}

/**
 * Get the auto-save debounce timeout
 */
export function getAutoSaveDebounceMs(): number {
  return SESSION_CONFIG.AUTO_SAVE_DEBOUNCE_MS;
}

/**
 * Get the manual save timeout
 */
export function getManualSaveTimeoutMs(): number {
  return SESSION_CONFIG.MANUAL_SAVE_TIMEOUT_MS;
}

/**
 * Get the maximum retry attempts
 */
export function getMaxRetryAttempts(): number {
  return SESSION_CONFIG.MAX_RETRY_ATTEMPTS;
}

/**
 * Get the retry delay
 */
export function getRetryDelayMs(): number {
  return SESSION_CONFIG.RETRY_DELAY_MS;
}
